import { isEqual } from 'lodash';
import { ABORTED, COMPLETED, LATE, NOT_LOGGED_IN } from 'constants/States';

export function busLocationsChanged(prevBusLocations, nextBusLocations) {
  if (prevBusLocations && nextBusLocations && prevBusLocations.length !== nextBusLocations.length) {
    return true; // The number of buses has changed
  } else if (!prevBusLocations || !nextBusLocations) {
    return true; // One of the arrays is null or undefined, indicating a change
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < prevBusLocations.length; i++) {
    const prevBus = prevBusLocations[i];
    const nextBus = nextBusLocations[i];

    const fieldsToCompare = {
      busId: prevBus.busId,
      actual: prevBus.actual,
      lastLocatedTime: prevBus.lastLocatedTime,
      coordinates:
        prevBus.location && prevBus.location.geometry && prevBus.location.geometry.coordinates,
    };

    const nextFieldsToCompare = {
      busId: nextBus.busId,
      actual: nextBus.actual,
      lastLocatedTime: nextBus.lastLocatedTime,
      coordinates:
        nextBus.location && nextBus.location.geometry && nextBus.location.geometry.coordinates,
    };

    // Use lodash's isEqual to perform a deep comparison of the specified fields
    if (!isEqual(fieldsToCompare, nextFieldsToCompare)) {
      return true; // At least one field has changed
    }
  }

  // No changes detected in any of the fields compared
  return false;
}

// Define a function to compare two floating-point numbers to 6 decimal places
export const areCoordinatesEqual = (coord1, coord2) => {
  const tolerance = 0.000001; // 11cm gps
  return Math.abs(coord1 - coord2) < tolerance;
};

export function markersChanged(prevMarkers, nextMarkers) {
  // Check if the number of markers has changed
  if (prevMarkers.length !== nextMarkers.length) {
    return true; // The number of markers has changed
  }

  // Perform a deep comparison of each marker's relevant fields
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < prevMarkers.length; i++) {
    const prevMarker = prevMarkers[i];
    const nextMarker = nextMarkers[i];

    // Check positions with custom logic
    if (
      !areCoordinatesEqual(prevMarker.position.lat, nextMarker.position.lat) ||
      !areCoordinatesEqual(prevMarker.position.lng, nextMarker.position.lng)
    ) {
      return true; // Position has changed
    }

    // Construct objects for each marker excluding the position for lodash comparison
    const prevMarkerFields = {
      information: prevMarker.information,
      isPositionActual: prevMarker.isPositionActual,
      lastLocatedTime: prevMarker.lastLocatedTime,
    };

    const nextMarkerFields = {
      information: nextMarker.information,
      isPositionActual: nextMarker.isPositionActual,
      lastLocatedTime: nextMarker.lastLocatedTime,
    };

    // Use lodash's isEqual to perform a deep comparison of the other specified fields
    if (!isEqual(prevMarkerFields, nextMarkerFields)) {
      return true; // At least one of the other fields has changed
    }
  }

  // No changes detected in any of the fields compared
  return false;
}

const CAN_NOT_LOCATE = [COMPLETED, ABORTED, LATE];
export const allowedToLocate = route => {
  const isNotAllowedState = CAN_NOT_LOCATE.includes(route.state);
  const isNotLoggedInAndInactive = route.state === NOT_LOGGED_IN && !route.active;
  return !(isNotAllowedState || isNotLoggedInAndInactive);
};

export const extractBusLocations = todayRoutesData =>
  (todayRoutesData &&
    todayRoutesData.flatMap(campus =>
      campus.routes
        .filter(route => route.location !== null && allowedToLocate(route))
        .map(route => route.location),
    )) ||
  [];
